// Festive themes (2026-10-04, v2 "pro" pass). One theme is active for everyone — chosen on the Theme tab,
// which only admin@test.com can see. "default" is the app exactly as it always looked.
// Look: characters are full-colour painted vector figures (skin, faces, shaded fabric, gold jewellery);
// effects — fireworks, fire, smoke, snow, petals — are real particle physics on one canvas
// (FestiveEngine.js); garlands, bulbs, diyas and lanterns are shaded vector art. Nothing here takes a
// click, and it all sits under every modal.
import React, { useEffect, useRef, useState } from 'react';
import { FxEngine, FIREWORK_COLORS } from './FestiveEngine';
import './FestiveTheme.css';

export const THEME_OWNER_EMAIL = 'admin@test.com';

export const FESTIVE_THEMES = [
    { key: 'default', label: 'Default', greeting: '', icon: '✨', blurb: 'The everyday look — no decorations.',
        swatch: ['#172536', '#6366f1', '#8b5cf6'] },
    { key: 'navratri', label: 'Navratri', greeting: 'Happy Navratri', icon: '🪘', blurb: 'Mata Rani on her lion, garba dancers, marigold toran and falling petals.',
        swatch: ['#3b0f4f', '#e11d74', '#f59e0b'] },
    { key: 'dussehra', label: 'Dussehra', greeting: 'Happy Dussehra', icon: '🏹', blurb: 'Ram ji vs Ravan — arrows clash mid-air, the 4th strikes Ravan, he burns, then fireworks.',
        swatch: ['#4a1206', '#dc2626', '#f59e0b'] },
    { key: 'diwali', label: 'Diwali', greeting: 'Happy Diwali', icon: '🪔', blurb: 'Glowing diyas, lit kandils, fairy lights and non-stop fireworks.',
        swatch: ['#3b0a0a', '#ea580c', '#facc15'] },
    { key: 'christmas', label: 'Christmas', greeting: 'Merry Christmas', icon: '🎄', blurb: "Santa's sleigh dropping gifts, a lit tree, falling snow and lights.",
        swatch: ['#0f3b2a', '#dc2626', '#16a34a'] },
];
export const isFestiveThemeKey = (k) => FESTIVE_THEMES.some(t => t.key === k);

export const useApplyFestiveTheme = (themeKey) => {
    useEffect(() => {
        const el = document.documentElement;
        if (themeKey && themeKey !== 'default') el.setAttribute('data-festive', themeKey);
        else el.removeAttribute('data-festive');
    }, [themeKey]);
};

const useViewportWidth = () => {
    const [w, setW] = useState(typeof window === 'undefined' ? 1200 : window.innerWidth);
    useEffect(() => {
        const on = () => setW(window.innerWidth);
        window.addEventListener('resize', on);
        return () => window.removeEventListener('resize', on);
    }, []);
    return w;
};

const reducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const rand = (a, b) => a + Math.random() * (b - a);

// Runs the particle canvas. `setup(engine)` once, `tick(engine, seconds, k)` every frame.
const useFx = (setup, tick) => {
    const canvasRef = useRef(null);
    const tickRef = useRef(tick);
    tickRef.current = tick;
    const setupRef = useRef(setup);
    useEffect(() => {
        if (reducedMotion() || !canvasRef.current) return undefined;
        const eng = new FxEngine(canvasRef.current);
        if (setupRef.current) setupRef.current(eng);
        let raf, last = performance.now();
        const t0 = last;
        const loop = (now) => {
            const k = Math.min(50, now - last) / 16.67;
            last = now;
            if (tickRef.current) tickRef.current(eng, (now - t0) / 1000, k);
            eng.step(k);
            eng.draw();
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        const onResize = () => eng.resize();
        window.addEventListener('resize', onResize);
        return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
    }, []);
    return canvasRef;
};
const FxCanvas = ({ canvasRef }) => <canvas ref={canvasRef} className="fx-canvas" />;

// ── Garlands ─────────────────────────────────────────────────────────────────────────────────
// Real marigold toran: festoons of shaded marigolds with mango leaves and hanging strands.
const MarigoldToran = ({ width, small }) => {
    const seg = small ? 92 : 132, h = small ? 32 : 44, n = Math.ceil(width / seg) + 1;
    const fl = small ? 0.62 : 0.85;
    return (
        <svg className="fx-toran2" width={width} height={h + (small ? 18 : 26)} viewBox={`0 0 ${width} ${h + (small ? 18 : 26)}`}>
            <defs>
                <radialGradient id="mgCore"><stop offset="0" stopColor="#fef3c7" /><stop offset=".5" stopColor="#f59e0b" /><stop offset="1" stopColor="#b45309" /></radialGradient>
                <radialGradient id="mgO" cx=".5" cy=".3"><stop offset="0" stopColor="#fdba74" /><stop offset=".6" stopColor="#f97316" /><stop offset="1" stopColor="#c2410c" /></radialGradient>
                <radialGradient id="mgY" cx=".5" cy=".3"><stop offset="0" stopColor="#fef08a" /><stop offset=".6" stopColor="#facc15" /><stop offset="1" stopColor="#ca8a04" /></radialGradient>
                <linearGradient id="mgLeaf" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#14532d" /><stop offset=".5" stopColor="#16a34a" /><stop offset="1" stopColor="#15803d" /></linearGradient>
                {['O', 'Y'].map(v => (
                    <symbol key={v} id={`mg${v}s`} viewBox="-12 -12 24 24" overflow="visible">
                        {Array.from({ length: 16 }).map((_, i) => <ellipse key={i} cx="0" cy="-7" rx="3.3" ry="4.8" fill={`url(#mg${v})`} stroke="#9a3412" strokeWidth=".25" transform={`rotate(${i * 22.5})`} />)}
                        {Array.from({ length: 11 }).map((_, i) => <ellipse key={`m${i}`} cx="0" cy="-4.4" rx="2.6" ry="3.6" fill={`url(#mg${v})`} stroke="#9a3412" strokeWidth=".2" transform={`rotate(${i * 32.7 + 10})`} />)}
                        <circle r="3.2" fill="url(#mgCore)" />
                    </symbol>
                ))}
                <symbol id="mgLeafS" viewBox="-6 0 12 26" overflow="visible">
                    <path d="M0 0 Q7 9 0 26 Q-7 9 0 0 Z" fill="url(#mgLeaf)" /><path d="M0 2 L0 24" stroke="#bbf7d0" strokeWidth=".6" opacity=".7" />
                </symbol>
            </defs>
            {Array.from({ length: n }).map((_, i) => {
                const x0 = i * seg, x1 = x0 + seg, cx = x0 + seg / 2, sag = h * 0.62;
                const pt = (t) => [(1 - t) * (1 - t) * x0 + 2 * (1 - t) * t * cx + t * t * x1, 2 * (1 - t) * t * sag + 3];
                return (
                    <g key={i}>
                        <path d={`M${x0} 3 Q${cx} ${sag * 2 + 3} ${x1} 3`} stroke="#7c2d12" strokeWidth="1.4" fill="none" />
                        {[0.12, 0.3, 0.5, 0.7, 0.88].map((t, j) => {
                            const [x, y] = pt(t);
                            return <use key={j} href={`#mg${j % 2 ? 'Y' : 'O'}s`} x={x - 12 * fl} y={y - 12 * fl} width={24 * fl} height={24 * fl} />;
                        })}
                        {i % 2 === 0 && (
                            <g className="fx-strand" style={{ animationDelay: `${(i % 4) * 0.4}s` }}>
                                <use href="#mgLeafS" x={cx - 6 * fl} y={sag + 4} width={12 * fl} height={22 * fl} />
                                <use href="#mgYs" x={cx - 9 * fl} y={sag + 10 * fl} width={18 * fl} height={18 * fl} />
                            </g>
                        )}
                    </g>
                );
            })}
        </svg>
    );
};

// Glass fairy bulbs with a real glow halo.
const FairyLights = ({ width, small, warm }) => {
    const seg = small ? 34 : 44, h = small ? 26 : 34, n = Math.ceil(width / seg) + 1;
    const colors = warm ? ['#fbbf24', '#fb923c', '#fde047', '#f59e0b'] : ['#ef4444', '#22c55e', '#facc15', '#3b82f6', '#ec4899', '#f97316'];
    return (
        <svg className="fx-lights2" width={width} height={h + 14} viewBox={`0 0 ${width} ${h + 14}`}>
            <defs>
                {colors.map((c, i) => (
                    <radialGradient key={i} id={`bulb${i}`} cx=".35" cy=".3" r=".75">
                        <stop offset="0" stopColor="#fff" /><stop offset=".25" stopColor={c} /><stop offset="1" stopColor={c} stopOpacity=".85" />
                    </radialGradient>
                ))}
                {colors.map((c, i) => (
                    <radialGradient key={`h${i}`} id={`halo${i}`}><stop offset="0" stopColor={c} stopOpacity=".55" /><stop offset="1" stopColor={c} stopOpacity="0" /></radialGradient>
                ))}
            </defs>
            <path d={Array.from({ length: n }).map((_, i) => `M${i * seg} 4 Q${i * seg + seg / 2} ${h * 0.55} ${(i + 1) * seg} 4`).join(' ')}
                stroke="#1f2937" strokeWidth="1.3" fill="none" />
            {Array.from({ length: n }).map((_, i) => {
                const x = i * seg + seg / 2, y = h * 0.28 + 4, ci = i % colors.length;
                return (
                    <g key={i}>
                        <circle className="fx-halo-pulse" cx={x} cy={y + 9} r={small ? 9 : 12} fill={`url(#halo${ci})`} style={{ animationDelay: `${(i * 0.53) % 2.4}s` }} />
                        <rect x={x - 2.6} y={y - 1} width="5.2" height="5" rx="1" fill="#374151" />
                        <ellipse cx={x} cy={y + 9} rx={small ? 3.6 : 4.6} ry={small ? 5.6 : 7} fill={`url(#bulb${ci})`} />
                    </g>
                );
            })}
        </svg>
    );
};

const Greeting = ({ text }) => text ? <div className="fx-greeting2"><span>{text}</span></div> : null;

// ── Characters (v3, full colour) ─────────────────────────────────────────────────────────────
// Painted, shaded vector figures: skin tones, faces, fabric with light and fold shadows, gold jewellery.
const GOLD = '#e8b923';
const GoldDefs = ({ id }) => (
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#fff1b8" /><stop offset=".45" stopColor="#f2c230" /><stop offset="1" stopColor="#a8740f" /></linearGradient>
);

// A small painted face (front view). cx/cy = face centre, r = face radius.
const Face = ({ cx, cy, r, lip = '#b4232c', brow = '#2b1607', bindi = true }) => (
    <g>
        <path d={`M${cx - r * 0.62} ${cy - r * 0.32} Q${cx - r * 0.38} ${cy - r * 0.5} ${cx - r * 0.14} ${cy - r * 0.34}`} stroke={brow} strokeWidth={r * 0.1} fill="none" strokeLinecap="round" />
        <path d={`M${cx + r * 0.14} ${cy - r * 0.34} Q${cx + r * 0.38} ${cy - r * 0.5} ${cx + r * 0.62} ${cy - r * 0.32}`} stroke={brow} strokeWidth={r * 0.1} fill="none" strokeLinecap="round" />
        {[-1, 1].map(s => (
            <g key={s}>
                <ellipse cx={cx + s * r * 0.38} cy={cy - r * 0.08} rx={r * 0.24} ry={r * 0.15} fill="#fff" />
                <circle cx={cx + s * r * 0.38} cy={cy - r * 0.07} r={r * 0.12} fill="#2b1607" />
                <circle cx={cx + s * r * 0.35} cy={cy - r * 0.11} r={r * 0.04} fill="#fff" />
                <path d={`M${cx + s * r * 0.13} ${cy - r * 0.1} Q${cx + s * r * 0.38} ${cy - r * 0.3} ${cx + s * r * 0.65} ${cy - r * 0.1}`} stroke="#140a04" strokeWidth={r * 0.07} fill="none" />
            </g>
        ))}
        <path d={`M${cx} ${cy + r * 0.02} Q${cx + r * 0.08} ${cy + r * 0.2} ${cx - r * 0.04} ${cy + r * 0.24}`} stroke="rgba(120,60,30,.55)" strokeWidth={r * 0.06} fill="none" />
        <path d={`M${cx - r * 0.24} ${cy + r * 0.44} Q${cx} ${cy + r * 0.6} ${cx + r * 0.24} ${cy + r * 0.44} Q${cx} ${cy + r * 0.5} ${cx - r * 0.24} ${cy + r * 0.44} Z`} fill={lip} />
        <ellipse cx={cx - r * 0.55} cy={cy + r * 0.25} rx={r * 0.18} ry={r * 0.1} fill="rgba(236,72,153,.18)" />
        <ellipse cx={cx + r * 0.55} cy={cy + r * 0.25} rx={r * 0.18} ry={r * 0.1} fill="rgba(236,72,153,.18)" />
        {bindi && <circle cx={cx} cy={cy - r * 0.48} r={r * 0.1} fill="#dc2626" />}
    </g>
);

const GARBA_PALETTES = [
    { id: 'gA', skirt: ['#c2185b', '#f59e0b', '#e11d74'], choli: '#15803d', dup: '#fb923c' },
    { id: 'gB', skirt: ['#1d4ed8', '#facc15', '#2563eb'], choli: '#dc2626', dup: '#ec4899' },
];
const GarbaDancer = ({ flip, palette = GARBA_PALETTES[0] }) => {
    const P = palette, id = (k) => `${P.id}-${k}`;
    return (
        <svg viewBox="0 0 130 212" className={`fx-garba ${flip ? 'fx-flip' : ''}`}>
            <defs>
                <linearGradient id={id('skin')} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#d9a274" /><stop offset="1" stopColor="#a96b40" /></linearGradient>
                <linearGradient id={id('skirt')} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor={P.skirt[0]} /><stop offset=".2" stopColor={P.skirt[2]} /><stop offset=".34" stopColor={P.skirt[1]} />
                    <stop offset=".5" stopColor={P.skirt[0]} /><stop offset=".66" stopColor={P.skirt[1]} /><stop offset=".8" stopColor={P.skirt[2]} /><stop offset="1" stopColor={P.skirt[0]} />
                </linearGradient>
                <linearGradient id={id('fold')} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#000" stopOpacity="0" /><stop offset="1" stopColor="#000" stopOpacity=".28" /></linearGradient>
                <linearGradient id={id('dup')} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={P.dup} stopOpacity=".95" /><stop offset="1" stopColor={P.dup} stopOpacity=".55" /></linearGradient>
                <linearGradient id={id('hair')} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#3b2414" /><stop offset="1" stopColor="#120804" /></linearGradient>
                <GoldDefs id={id('gold')} />
            </defs>
            {/* flowing dupatta behind */}
            <path d="M58 60 C40 62 24 80 14 104 C26 92 42 80 56 74 Z" fill={`url(#${id('dup')})`} />
            <path d="M58 60 C40 62 24 80 14 104" stroke={`url(#${id('gold')})`} strokeWidth="1.4" fill="none" />
            {/* ghagra */}
            <g className="fx-skirt2">
                <path d="M54 92 C44 110 24 140 6 178 C18 186 30 180 38 190 C50 182 60 194 70 186 C82 196 94 184 106 190 C114 182 122 186 128 176 C112 144 92 114 80 92 Z" fill={`url(#${id('skirt')})`} />
                <path d="M54 92 C44 110 24 140 6 178 C18 186 30 180 38 190 C50 182 60 194 70 186 C82 196 94 184 106 190 C114 182 122 186 128 176 C112 144 92 114 80 92 Z" fill={`url(#${id('fold')})`} />
                {[[60, 22, 180], [64, 44, 186], [67, 66, 188], [70, 88, 188], [74, 108, 184]].map(([x0, x1, y1], i) => (
                    <path key={i} d={`M${x0} 96 Q${(x0 + x1) / 2 + (i - 2) * 3} 140 ${x1} ${y1}`} stroke="rgba(0,0,0,.16)" strokeWidth="2.6" fill="none" />
                ))}
                <path d="M9 174 C18 182 30 177 38 186 C50 178 60 190 70 182 C82 192 94 180 106 186 C114 178 122 182 126 173" stroke={`url(#${id('gold')})`} strokeWidth="3.4" fill="none" />
                <path d="M20 152 C46 162 86 164 114 150" stroke={`url(#${id('gold')})`} strokeWidth="1.6" fill="none" />
                {[24, 36, 48, 60, 72, 84, 96, 108].map((x, i) => (
                    <g key={x}><circle cx={x} cy={160 + Math.sin(i) * 2} r="2" fill="#e5e7eb" /><circle cx={x - 0.6} cy={159.4 + Math.sin(i) * 2} r=".7" fill="#fff" /></g>
                ))}
            </g>
            {/* midriff, choli, neck */}
            <path d="M57 74 L77 74 L80 93 L54 93 Z" fill={`url(#${id('skin')})`} />
            <path d="M54 92 L80 92" stroke={`url(#${id('gold')})`} strokeWidth="2.6" />
            <path d="M58 58 C62 54 72 54 76 58 L78 76 C72 78 62 78 56 76 Z" fill={P.choli} />
            <path d="M56 76 C62 78 72 78 78 76" stroke={`url(#${id('gold')})`} strokeWidth="1.8" fill="none" />
            {[61, 67, 73].map(x => <circle key={x} cx={x} cy="68" r="1.3" fill="#e5e7eb" />)}
            <rect x="63" y="44" width="7" height="14" rx="3" fill={`url(#${id('skin')})`} />
            {/* raised arms with bangles */}
            <path d="M60 60 C52 50 46 38 40 24 L45 22 C50 36 56 48 64 58 Z" fill={`url(#${id('skin')})`} />
            <path d="M74 60 C82 50 88 38 94 24 L89 22 C84 36 78 48 70 58 Z" fill={`url(#${id('skin')})`} />
            <path d="M42 30 L47 29 M43.5 33 L48.5 32 M87 29 L92 30 M85.5 32 L90.5 33" stroke={`url(#${id('gold')})`} strokeWidth="1.8" strokeLinecap="round" />
            <path d="M59 61 C64 67 70 67 75 61" stroke={`url(#${id('gold')})`} strokeWidth="1.6" fill="none" />
            <circle cx="67" cy="67" r="1.6" fill="#dc2626" />
            {/* head */}
            <circle cx="55" cy="29" r="7" fill={`url(#${id('hair')})`} />
            {[0, 60, 120, 180, 240, 300].map(a => <circle key={a} cx={55 + Math.cos((a * Math.PI) / 180) * 7} cy={29 + Math.sin((a * Math.PI) / 180) * 7} r="1.5" fill="#fff" />)}
            <path d="M55 36 C54 44 58 52 60 58" stroke={`url(#${id('hair')})`} strokeWidth="4" fill="none" strokeLinecap="round" />
            <circle cx="66" cy="36" r="10" fill={`url(#${id('skin')})`} />
            <path d="M56 35 C55 26 61 22 66 22 C72 22 77 26 76 35 C74 29 70 27 66 27 C62 27 58 29 56 35 Z" fill={`url(#${id('hair')})`} />
            <Face cx={66} cy={37} r={9} />
            <path d="M66 22 L66 28" stroke={`url(#${id('gold')})`} strokeWidth="1" /><circle cx="66" cy="29" r="1.4" fill={`url(#${id('gold')})`} />
            <circle cx="56.6" cy="40" r="1.4" fill={GOLD} /><circle cx="75.4" cy="40" r="1.4" fill={GOLD} />
            {/* dandiya sticks */}
            <g className="fx-sticks2" strokeLinecap="round">
                <path d="M34 32 L52 4" stroke="#b91c1c" strokeWidth="3.6" /><path d="M100 32 L82 4" stroke="#b91c1c" strokeWidth="3.6" />
                <path d="M37 27 L40 22 M44 16 L47 11 M97 27 L94 22 M90 16 L87 11" stroke="#facc15" strokeWidth="3.6" />
                <path d="M40 22 L44 16 M94 22 L90 16" stroke="#16a34a" strokeWidth="3.6" />
            </g>
        </svg>
    );
};

// Eight arms fanning out symmetrically from the shoulders, each holding her weapon.
const MATA_ARMS = [
    { a: -62, side: 1, item: 'trishul' }, { a: -32, side: 1, item: 'sword' }, { a: -4, side: 1, item: 'chakra' }, { a: 26, side: 1, item: 'lotus' },
    { a: -118, side: -1, item: 'conch' }, { a: -148, side: -1, item: 'bow' }, { a: -176, side: -1, item: 'mace' }, { a: 154, side: -1, item: 'shield' },
];
const deg = (d) => (d * Math.PI) / 180;
const MataArm = ({ a, side, item }) => {
    const sx = side > 0 ? 141 : 119, sy = 66, L = 42;
    const ux = Math.cos(deg(a)), uy = Math.sin(deg(a)), px = -uy, py = ux;
    const hx = sx + ux * L, hy = sy + uy * L, ex = sx + ux * L * 0.5 + px * 3 * side, ey = sy + uy * L * 0.5 + py * 3 * side;
    const w0 = 3.5, w1 = 2.1;
    const arm = `M${sx + px * w0} ${sy + py * w0} Q${ex + px * 2.8} ${ey + py * 2.8} ${hx + px * w1} ${hy + py * w1} L${hx - px * w1} ${hy - py * w1} Q${ex - px * 2.8} ${ey - py * 2.8} ${sx - px * w0} ${sy - py * w0} Z`;
    const rot = `rotate(${a + 90} ${hx} ${hy})`;
    let held = null;
    if (item === 'trishul') held = <g transform={rot}><path d={`M${hx} ${hy + 14} L${hx} ${hy - 30}`} stroke="url(#mGold)" strokeWidth="2.4" />
        <path d={`M${hx - 8} ${hy - 24} C${hx - 8} ${hy - 32} ${hx - 4} ${hy - 36} ${hx} ${hy - 41} C${hx + 4} ${hy - 36} ${hx + 8} ${hy - 32} ${hx + 8} ${hy - 24} M${hx} ${hy - 41} L${hx} ${hy - 26}`} stroke="url(#mGold)" strokeWidth="2.2" fill="none" />
        <circle cx={hx} cy={hy - 22} r="1.8" fill="#dc2626" /></g>;
    if (item === 'sword') held = <g transform={rot}><path d={`M${hx} ${hy} C${hx + 4} ${hy - 12} ${hx + 5} ${hy - 22} ${hx + 1} ${hy - 33} C${hx - 2} ${hy - 22} ${hx - 2} ${hy - 12} ${hx} ${hy}`} fill="url(#mSteel)" />
        <path d={`M${hx - 5} ${hy - 1} L${hx + 5} ${hy - 1}`} stroke="url(#mGold)" strokeWidth="2.6" /></g>;
    if (item === 'chakra') held = <g transform={rot}><g className="fx-chakra2"><circle cx={hx} cy={hy - 10} r="8" fill="none" stroke="url(#mGold)" strokeWidth="2.8" />
        <circle cx={hx} cy={hy - 10} r="2.4" fill="#dc2626" />
        {Array.from({ length: 8 }).map((_, i) => <path key={i} d={`M${hx} ${hy - 19} L${hx} ${hy - 22.5}`} stroke="url(#mGold)" strokeWidth="2.2" transform={`rotate(${i * 45} ${hx} ${hy - 10})`} />)}</g></g>;
    if (item === 'lotus') held = <g transform={rot}>{[-44, -22, 0, 22, 44].map(r => <ellipse key={r} cx={hx} cy={hy - 8} rx="2.8" ry="7.2" fill={r === 0 ? '#f9a8d4' : '#ec4899'} transform={`rotate(${r} ${hx} ${hy - 2})`} />)}
        <circle cx={hx} cy={hy - 3} r="1.8" fill="#facc15" /></g>;
    if (item === 'conch') held = <g transform={rot}><path d={`M${hx - 4} ${hy - 2} C${hx - 8} ${hy - 12} ${hx - 2} ${hy - 21} ${hx + 3} ${hy - 19} C${hx + 8} ${hy - 12} ${hx + 5} ${hy - 4} ${hx - 4} ${hy - 2} Z`} fill="#fffbeb" stroke="#d6b77a" strokeWidth="1" />
        <path d={`M${hx - 2} ${hy - 6} Q${hx + 2} ${hy - 12} ${hx + 1} ${hy - 17}`} stroke="#d6b77a" strokeWidth=".9" fill="none" /></g>;
    if (item === 'bow') held = <g transform={rot}><path d={`M${hx - 14} ${hy - 6} Q${hx} ${hy - 22} ${hx + 14} ${hy - 6}`} stroke="#7c4a1e" strokeWidth="2.6" fill="none" />
        <path d={`M${hx - 14} ${hy - 6} L${hx + 14} ${hy - 6}`} stroke="#e7e5e4" strokeWidth=".9" /></g>;
    if (item === 'mace') held = <g transform={rot}><path d={`M${hx} ${hy + 6} L${hx} ${hy - 16}`} stroke="#7c4a1e" strokeWidth="2.6" />
        <circle cx={hx} cy={hy - 21} r="6.2" fill="url(#mGold)" /><circle cx={hx - 2} cy={hy - 23} r="1.8" fill="#fff7d6" opacity=".8" /></g>;
    if (item === 'shield') held = <g transform={rot}><circle cx={hx} cy={hy - 8} r="8.8" fill="#b91c1c" stroke="url(#mGold)" strokeWidth="1.8" />
        <circle cx={hx} cy={hy - 8} r="4.6" fill="none" stroke="url(#mGold)" strokeWidth="1" /><circle cx={hx} cy={hy - 8} r="1.8" fill="url(#mGold)" /></g>;
    return (
        <g>
            <path d={arm} fill="url(#mSkin)" />
            <circle cx={hx} cy={hy} r="2.8" fill="url(#mSkin)" />
            <path d={`M${hx - ux * 6 + px * 2.6} ${hy - uy * 6 + py * 2.6} L${hx - ux * 6 - px * 2.6} ${hy - uy * 6 - py * 2.6} M${hx - ux * 8.5 + px * 2.7} ${hy - uy * 8.5 + py * 2.7} L${hx - ux * 8.5 - px * 2.7} ${hy - uy * 8.5 - py * 2.7}`} stroke="url(#mGold)" strokeWidth="1.5" />
            {held}
        </g>
    );
};

// Mane outline: a ring of soft locks around the lion's face.
const MANE_FRONT = (() => {
    const cx = 130, cy = 160, n = 22, pts = [];
    for (let i = 0; i < n; i++) {
        const a0 = (i / n) * Math.PI * 2 - Math.PI / 2, a1 = ((i + 0.5) / n) * Math.PI * 2 - Math.PI / 2;
        pts.push([cx + Math.cos(a0) * 44, cy + Math.sin(a0) * 46, cx + Math.cos(a1) * 54, cy + Math.sin(a1) * 56]);
    }
    let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
    pts.forEach((p, i) => { const q = pts[(i + 1) % n]; d += ` Q${p[2].toFixed(1)} ${p[3].toFixed(1)} ${q[0].toFixed(1)} ${q[1].toFixed(1)}`; });
    return `${d} Z`;
})();

// Mata Rani on her lion — both facing straight out at the viewer (2026-10-05).
const MataOnLion = () => (
    <div className="fx-mata2">
        <div className="fx-aura"><div className="fx-aura-rays" /></div>
        <svg viewBox="0 0 260 250">
            <defs>
                <linearGradient id="lBody" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#efc06a" /><stop offset=".6" stopColor="#d79a3d" /><stop offset="1" stopColor="#b8782a" /></linearGradient>
                <radialGradient id="lMane" cx=".5" cy=".45" r=".6"><stop offset="0" stopColor="#d0782a" /><stop offset=".65" stopColor="#97501a" /><stop offset="1" stopColor="#5e2d0b" /></radialGradient>
                <radialGradient id="lHead" cx=".5" cy=".4" r=".7"><stop offset="0" stopColor="#f8d590" /><stop offset="1" stopColor="#d79a3d" /></radialGradient>
                <linearGradient id="mSkin" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#f3c8a2" /><stop offset="1" stopColor="#d59a6c" /></linearGradient>
                <linearGradient id="mSaree" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#ef4444" /><stop offset=".55" stopColor="#c81e1e" /><stop offset="1" stopColor="#8f1414" /></linearGradient>
                <linearGradient id="mSteel" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#94a3b8" /><stop offset=".5" stopColor="#f1f5f9" /><stop offset="1" stopColor="#94a3b8" /></linearGradient>
                <GoldDefs id="mGold" />
            </defs>
            {/* lion's back behind, tail curling up on one side */}
            <g className="fx-tail2"><path d="M196 150 C226 142 238 112 224 90" stroke="#d79a3d" strokeWidth="6" fill="none" strokeLinecap="round" />
                <path d="M224 90 C216 88 214 76 222 72 C230 68 236 78 230 86 C228 89 226 90 224 90 Z" fill="url(#lMane)" /></g>
            <path d="M58 178 C56 140 88 120 130 120 C172 120 204 140 202 178 Z" fill="url(#lBody)" />
            {/* Mata: hair behind, arms behind, saree spread over the lion's back */}
            <path d="M120 40 C110 56 108 80 110 104 L124 98 C120 80 120 60 124 46 Z" fill="#1a0d06" />
            <path d="M140 40 C150 56 152 80 150 104 L136 98 C140 80 140 60 136 46 Z" fill="#1a0d06" />
            {MATA_ARMS.map((arm, i) => <MataArm key={i} {...arm} />)}
            <path d="M80 134 C88 108 108 98 130 98 C152 98 172 108 180 134 C166 128 152 136 130 132 C108 136 94 128 80 134 Z" fill="url(#mSaree)" />
            <path d="M82 132 C96 126 108 134 130 130 C152 134 164 126 178 132" stroke="url(#mGold)" strokeWidth="3" fill="none" />
            {[96, 112, 130, 148, 164].map(x => <circle key={x} cx={x} cy="118" r="1.4" fill={GOLD} />)}
            <path d="M114 62 C120 58 140 58 146 62 L150 88 C150 95 152 101 154 106 L106 106 C108 101 110 95 110 88 Z" fill="url(#mSaree)" />
            <path d="M116 62 L150 104" stroke="url(#mGold)" strokeWidth="4.2" />
            <path d="M110 88 C122 92 138 92 150 88" stroke="url(#mGold)" strokeWidth="1.6" fill="none" />
            <rect x="126" y="46" width="8" height="14" rx="3" fill="url(#mSkin)" />
            <path d="M120 60 C125 68 135 68 140 60" stroke="url(#mGold)" strokeWidth="2" fill="none" />
            <path d="M122 62 C126 72 134 72 138 62" stroke="url(#mGold)" strokeWidth="1.3" fill="none" />
            <circle cx="130" cy="71" r="1.9" fill="#dc2626" />
            <circle cx="130" cy="39" r="10.5" fill="url(#mSkin)" />
            <path d="M119.5 38 C119 29 124 26 130 26 C136 26 141 29 140.5 38 C138 32 134.5 30.5 130 30.5 C125.5 30.5 122 32 119.5 38 Z" fill="#1a0d06" />
            <Face cx={130} cy={40} r={9.4} />
            <path d="M130 31.5 L130 35.5" stroke="#dc2626" strokeWidth="1.5" />
            <circle cx="133.8" cy="43.6" r="1.4" fill="none" stroke={GOLD} strokeWidth=".75" />
            <circle cx="119.4" cy="43" r="1.8" fill={GOLD} /><circle cx="140.6" cy="43" r="1.8" fill={GOLD} />
            <path d="M119 31 C118 22 122 12 130 3 C138 12 142 22 141 31 Z" fill="url(#mGold)" />
            <path d="M121 26 L139 26" stroke="#a8740f" strokeWidth="1" />
            <circle cx="130" cy="15" r="2.4" fill="#dc2626" /><circle cx="124.5" cy="23" r="1.3" fill="#16a34a" /><circle cx="135.5" cy="23" r="1.3" fill="#16a34a" />
            {[122, 126, 130, 134, 138].map(x => <circle key={x} cx={x} cy="29.6" r=".85" fill="#fff7d6" />)}
            {/* lion: front legs, mane, face — looking straight out */}
            <g className="fx-lion-breathe">
                <path d="M92 170 L88 232 Q88 240 98 240 L114 240 Q118 236 114 230 L112 172 Z" fill="url(#lBody)" />
                <path d="M168 170 L172 232 Q172 240 162 240 L146 240 Q142 236 146 230 L148 172 Z" fill="url(#lBody)" />
                <path d="M92 238 l2 3 M98 238 l1 3 M104 238 l0 3 M168 238 l-2 3 M162 238 l-1 3 M156 238 l0 3" stroke="#6b4513" strokeWidth="1.3" />
                <path d={MANE_FRONT} fill="url(#lMane)" />
                <path d="M100 128 C110 140 112 156 104 170 M160 128 C150 140 148 156 156 170 M116 118 C122 128 124 136 122 144 M144 118 C138 128 136 136 138 144 M110 196 C118 204 142 204 150 196" stroke="#e08a35" strokeWidth="1.8" fill="none" opacity=".7" />
                <circle cx="110" cy="128" r="7.5" fill="#d79a3d" /><circle cx="110" cy="128" r="3.8" fill="#b5653b" />
                <circle cx="150" cy="128" r="7.5" fill="#d79a3d" /><circle cx="150" cy="128" r="3.8" fill="#b5653b" />
                <path d="M106 146 C106 128 116 122 130 122 C144 122 154 128 154 146 C156 162 150 176 140 184 L120 184 C110 176 104 162 106 146 Z" fill="url(#lHead)" />
                <path d="M126 140 C127 150 127 156 126 160 L134 160 C133 156 133 150 134 140 Z" fill="#fbe2ac" />
                <path d="M114 140 Q120 136 125 140 M135 140 Q140 136 146 140" stroke="#8a4b14" strokeWidth="1.6" fill="none" />
                <ellipse cx="120" cy="145" rx="4.8" ry="3.2" fill="#fbbf24" /><ellipse cx="120" cy="145" rx="1.3" ry="2.8" fill="#1c1003" />
                <ellipse cx="140" cy="145" rx="4.8" ry="3.2" fill="#fbbf24" /><ellipse cx="140" cy="145" rx="1.3" ry="2.8" fill="#1c1003" />
                <circle cx="119" cy="144" r=".8" fill="#fff" /><circle cx="139" cy="144" r=".8" fill="#fff" />
                <circle cx="123" cy="170" r="8" fill="#f8e2b4" /><circle cx="137" cy="170" r="8" fill="#f8e2b4" />
                <path d="M123 160 L137 160 L130 167 Z" fill="#4a2a18" />
                <path d="M130 167 L130 172 M123 176 Q130 180 137 176" stroke="#5b2d0c" strokeWidth="1.4" fill="none" />
                {[[119, 168], [117, 172], [120, 175], [141, 168], [143, 172], [140, 175]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r=".8" fill="#7a4512" />)}
                <path d="M112 170 L100 167 M112 173 L100 174 M148 170 L160 167 M148 173 L160 174" stroke="#fff7e0" strokeWidth=".6" opacity=".8" />
            </g>
        </svg>
    </div>
);

const RamArcher = ({ innerRef }) => (
    <div className="fx-ram2" ref={innerRef}>
        <div className="fx-sunglow" />
        <svg viewBox="0 0 170 218">
            <defs>
                <linearGradient id="rSkin" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#6c9be0" /><stop offset="1" stopColor="#3d66ad" /></linearGradient>
                <linearGradient id="rDhoti" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#fde047" /><stop offset="1" stopColor="#d29b06" /></linearGradient>
                <linearGradient id="rWood" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#9a6331" /><stop offset="1" stopColor="#5c3a1a" /></linearGradient>
                <linearGradient id="rQuiver" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#8b5a2b" /><stop offset="1" stopColor="#4e2f12" /></linearGradient>
                <GoldDefs id="rGold" />
            </defs>
            <path d="M60 54 L70 52 L62 100 L52 100 Z" fill="url(#rQuiver)" />
            <path d="M58 62 L68 60 M55 86 L65 84" stroke="url(#rGold)" strokeWidth="1.8" />
            {[[58, 50, 54, 38], [62, 50, 60, 36], [66, 50, 66, 38]].map(([a, b, c, d], i) => (
                <g key={i}><path d={`M${a} ${b} L${c} ${d}`} stroke="#7c4a1e" strokeWidth="1.6" /><path d={`M${c - 2} ${d + 4} L${c} ${d} L${c + 2} ${d + 4}`} stroke="#dc2626" strokeWidth="1.6" fill="none" /></g>
            ))}
            <path d="M74 124 C70 146 64 166 58 186 L52 204 L40 210 L64 211 L66 208 C66 200 70 188 74 176 C80 160 84 142 86 128 Z" fill="url(#rSkin)" />
            <path d="M86 126 C98 140 108 152 116 166 C120 176 118 192 116 204 L128 209 L106 210 L106 204 C108 192 108 180 104 172 C96 158 86 146 76 132 Z" fill="url(#rSkin)" />
            <path d="M54 200 L64 199 M107 200 L117 199" stroke="url(#rGold)" strokeWidth="2" />
            <path d="M66 104 C80 98 96 100 104 108 C108 120 110 132 112 142 C98 136 84 136 64 146 C64 132 64 118 66 104 Z" fill="url(#rDhoti)" />
            <path d="M74 108 Q72 124 70 140 M86 106 Q88 122 90 138 M98 110 Q102 124 106 138" stroke="rgba(146,96,0,.45)" strokeWidth="1.6" fill="none" />
            <path d="M64 146 C84 136 98 136 112 142" stroke="#dc2626" strokeWidth="2.6" fill="none" />
            <path d="M72 56 C80 52 94 52 100 58 C100 72 98 88 96 104 L70 106 C70 90 70 72 72 56 Z" fill="url(#rSkin)" />
            <path d="M74 58 L97 102" stroke="#f97316" strokeWidth="6" opacity=".92" />
            <path d="M76 58 L96 100" stroke="url(#rGold)" strokeWidth="1" />
            <path d="M80 56 L92 98" stroke="#f8fafc" strokeWidth=".8" opacity=".8" />
            <path d="M66 106 C80 100 96 102 104 110" stroke="url(#rGold)" strokeWidth="3" fill="none" />
            <rect x="82" y="44" width="8" height="14" rx="3" fill="url(#rSkin)" />
            <path d="M76 60 C82 68 92 68 98 60" stroke="url(#rGold)" strokeWidth="1.8" fill="none" />
            {[78, 82, 86, 90, 94].map((x, i) => <circle key={x} cx={x} cy={63 + (i === 2 ? 3 : i % 2 ? 2 : 0.5)} r="1.3" fill={['#16a34a', '#dc2626', '#facc15', '#dc2626', '#16a34a'][i]} />)}
            <path d="M78 36 C70 46 66 62 64 80 C72 70 76 56 82 44 Z" fill="#160b04" />
            <circle cx="86" cy="38" r="10" fill="url(#rSkin)" />
            <path d="M76.5 37 C76 29 80 26 86 26 C92 26 96 29 95.5 37 C93 31 90 30 86 30 C82 30 79 31 76.5 37 Z" fill="#160b04" />
            <Face cx={87} cy={39} r={8.4} lip="#9f1239" brow="#160b04" bindi={false} />
            <path d="M84.6 31 L86 34.6 L87.4 31" stroke="#fff" strokeWidth="1" fill="none" /><path d="M86 31 L86 34" stroke="#dc2626" strokeWidth=".9" />
            <circle cx="77" cy="41" r="1.8" fill="url(#rGold)" />
            <path d="M77 34 C77 24 80 16 86 3 C92 16 95 24 95 34 Z" fill="url(#rGold)" />
            <path d="M78 28 L94 28" stroke="#a8740f" strokeWidth="1" />
            <circle cx="86" cy="18" r="2" fill="#dc2626" /><circle cx="81.5" cy="25" r="1.1" fill="#16a34a" /><circle cx="90.5" cy="25" r="1.1" fill="#16a34a" />
            <path d="M96 58 C112 60 128 61 142 61 L142 67 C128 67 112 66 96 66 Z" fill="url(#rSkin)" />
            <path d="M108 59 L108 66 M136 60 L136 67" stroke="url(#rGold)" strokeWidth="2" />
            <path d="M124 2 C152 30 152 98 124 126" stroke="url(#rWood)" strokeWidth="5" strokeLinecap="round" fill="none" />
            <path d="M141 58 L143 70" stroke="#b91c1c" strokeWidth="5" />
            <path d="M124 2 L127 7 M124 126 L127 121" stroke="url(#rGold)" strokeWidth="2.8" strokeLinecap="round" />
            <g className="fx-draw-arm">
                <path d="M76 56 C68 54 62 54 56 55 L56 62 C62 62 68 62 76 64 Z" fill="url(#rSkin)" />
                <path d="M56 55 C66 52 78 50 90 48 L91 54 C78 56 66 59 56 62 Z" fill="url(#rSkin)" />
                <path d="M82 50 L83 56" stroke="url(#rGold)" strokeWidth="1.8" />
            </g>
            <path className="fx-string-drawn" d="M124 2 L90 50 L124 126" stroke="#e7e5e4" strokeWidth="1.1" fill="none" />
            <path className="fx-string-rest" d="M124 2 L124 126" stroke="#e7e5e4" strokeWidth="1.1" fill="none" />
            <g className="fx-nocked"><path d="M88 50 L154 64" stroke="#7c4a1e" strokeWidth="2.2" /><path d="M160 65 L150 59 L150 69 Z" fill="#94a3b8" />
                <path d="M88 50 L82 46 M88 50 L83 54" stroke="#dc2626" strokeWidth="1.6" /></g>
        </svg>
    </div>
);

const RAVAN_HEADS = [[0, 15], [-25, 12], [25, 12], [-47, 11], [47, 11], [-68, 10], [68, 10], [-88, 9], [88, 9]];
const RavanEffigy = ({ innerRef }) => (
    <div className="fx-ravan2" ref={innerRef}>
        <svg viewBox="0 0 220 322">
            <defs>
                <linearGradient id="vRobe" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#991b1b" /><stop offset=".5" stopColor="#dc2626" /><stop offset="1" stopColor="#991b1b" /></linearGradient>
                <radialGradient id="vFace" cx=".45" cy=".35" r=".7"><stop offset="0" stopColor="#fde4a8" /><stop offset="1" stopColor="#e7a447" /></radialGradient>
                <linearGradient id="vSteel" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#94a3b8" /><stop offset=".5" stopColor="#f8fafc" /><stop offset="1" stopColor="#94a3b8" /></linearGradient>
                <linearGradient id="vBamboo" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#c8a165" /><stop offset="1" stopColor="#8a6a37" /></linearGradient>
                <linearGradient id="vWood" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#6b3f1c" /><stop offset="1" stopColor="#3f220c" /></linearGradient>
                <GoldDefs id="vGold" />
            </defs>
            {/* bamboo stand */}
            <rect x="30" y="282" width="160" height="12" rx="2" fill="url(#vBamboo)" />
            <path d="M52 294 L42 320 M168 294 L178 320 M46 308 L174 308" stroke="url(#vBamboo)" strokeWidth="4" />
            {/* robe */}
            <path d="M70 120 L150 120 L180 282 L40 282 Z" fill="url(#vRobe)" />
            <path d="M84 124 L74 280 M136 124 L146 280" stroke="url(#vGold)" strokeWidth="3" />
            {[150, 186, 222, 258].map(y => <path key={y} d={`M${62 - (y - 150) * 0.12} ${y} L${158 + (y - 150) * 0.12} ${y}`} stroke="#15803d" strokeWidth="2.2" strokeDasharray="6 4" />)}
            {[160, 196, 232, 266].map(y => [96, 110, 124].map(x => <path key={`${x}-${y}`} d={`M${x} ${y - 5} L${x + 5} ${y} L${x} ${y + 5} L${x - 5} ${y} Z`} fill="url(#vGold)" />))}
            <path d="M42 280 L178 280" stroke="url(#vGold)" strokeWidth="4" />
            {/* arms, sword, shield */}
            <path d="M164 100 C176 116 188 136 194 152 C197 160 199 166 198 172" stroke="#b91c1c" strokeWidth="13" strokeLinecap="round" fill="none" />
            <circle cx="198" cy="172" r="6" fill="url(#vFace)" />
            <path d="M198 168 L216 94" stroke="url(#vSteel)" strokeWidth="4.5" strokeLinecap="round" />
            <path d="M189 156 L208 162" stroke="url(#vGold)" strokeWidth="4.5" strokeLinecap="round" />
            {/* bow arm, aimed at Ram ji (2026-10-05: Ravan fights back) */}
            <path d="M56 100 C42 108 26 116 8 122" stroke="#b91c1c" strokeWidth="13" strokeLinecap="round" fill="none" />
            <path d="M16 78 Q-8 123 16 168" stroke="url(#vWood)" strokeWidth="5" strokeLinecap="round" fill="none" />
            <path d="M16 78 L15 85 M16 168 L15 161" stroke="url(#vGold)" strokeWidth="3.2" strokeLinecap="round" />
            <circle cx="4" cy="123" r="6" fill="url(#vFace)" />
            <path className="fx-rv-string-rest" d="M16 78 L16 168" stroke="#e7e5e4" strokeWidth="1.1" />
            <path className="fx-rv-string-drawn" d="M16 78 L40 123 L16 168" stroke="#e7e5e4" strokeWidth="1.1" fill="none" />
            <g className="fx-rv-nocked"><path d="M40 123 L-22 123" stroke="#3f1d0b" strokeWidth="2.4" /><path d="M-28 123 L-18 117 L-18 129 Z" fill="#94a3b8" />
                <path d="M40 123 L46 118 M40 123 L46 128" stroke="#7c3aed" strokeWidth="1.8" /></g>
            {/* shoulders + collar */}
            <path d="M52 92 C70 82 150 82 168 92 C178 96 180 106 174 112 L152 112 L150 124 L70 124 L68 112 L46 112 C40 106 42 96 52 92 Z" fill="#15803d" />
            <path d="M54 94 C74 86 146 86 166 94" stroke="url(#vGold)" strokeWidth="3" fill="none" />
            <path d="M68 112 L152 112" stroke="url(#vGold)" strokeWidth="2.4" />
            <path d="M98 74 L122 74 L124 90 L96 90 Z" fill="#e7a447" />
            {/* ten heads */}
            {RAVAN_HEADS.map(([dx, r], i) => {
                const x = 110 + dx, y = 62 + (i === 0 ? 0 : 4);
                const crown = `M${x - r * 0.9} ${y - r * 0.5} L${x - r * 0.75} ${y - r * 1.6} L${x - r * 0.35} ${y - r * 1.1} L${x} ${y - r * 2.15} L${x + r * 0.35} ${y - r * 1.1} L${x + r * 0.75} ${y - r * 1.6} L${x + r * 0.9} ${y - r * 0.5} Z`;
                return (
                    <g key={i}>
                        <ellipse cx={x - r * 1.02} cy={y + 1} rx={r * 0.22} ry={r * 0.4} fill="#e7a447" />
                        <ellipse cx={x + r * 1.02} cy={y + 1} rx={r * 0.22} ry={r * 0.4} fill="#e7a447" />
                        <ellipse cx={x} cy={y} rx={r} ry={r * 1.08} fill="url(#vFace)" stroke="#9a5a14" strokeWidth=".6" />
                        <path d={`M${x - r * 0.7} ${y - r * 0.32} L${x - r * 0.12} ${y - r * 0.18} M${x + r * 0.7} ${y - r * 0.32} L${x + r * 0.12} ${y - r * 0.18}`} stroke="#111" strokeWidth={r * 0.14} strokeLinecap="round" />
                        <ellipse cx={x - r * 0.38} cy={y - r * 0.02} rx={r * 0.22} ry={r * 0.17} fill="#fff" />
                        <ellipse cx={x + r * 0.38} cy={y - r * 0.02} rx={r * 0.22} ry={r * 0.17} fill="#fff" />
                        <circle className="fx-eye" cx={x - r * 0.36} cy={y} r={r * 0.1} fill="#111" />
                        <circle className="fx-eye" cx={x + r * 0.36} cy={y} r={r * 0.1} fill="#111" />
                        <path d={`M${x - r * 0.75} ${y + r * 0.5} C${x - r * 0.45} ${y + r * 0.18} ${x - r * 0.15} ${y + r * 0.32} ${x} ${y + r * 0.36} C${x + r * 0.15} ${y + r * 0.32} ${x + r * 0.45} ${y + r * 0.18} ${x + r * 0.75} ${y + r * 0.5} C${x + r * 0.5} ${y + r * 0.42} ${x + r * 0.2} ${y + r * 0.46} ${x} ${y + r * 0.46} C${x - r * 0.2} ${y + r * 0.46} ${x - r * 0.5} ${y + r * 0.42} ${x - r * 0.75} ${y + r * 0.5} Z`} fill="#111" />
                        <path d={`M${x - r * 0.22} ${y + r * 0.66} Q${x} ${y + r * 0.76} ${x + r * 0.22} ${y + r * 0.66}`} stroke="#b91c1c" strokeWidth={r * 0.12} fill="none" strokeLinecap="round" />
                        <path d={crown} fill="url(#vGold)" stroke="#a8740f" strokeWidth=".6" />
                        <circle cx={x} cy={y - r * 1.1} r={r * 0.17} fill={i % 2 ? '#16a34a' : '#dc2626'} />
                    </g>
                );
            })}
        </svg>
    </div>
);

const SleighColour = () => (
    <svg viewBox="0 0 390 130">
        <defs>
            <linearGradient id="sDeer" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#9a6436" /><stop offset="1" stopColor="#5e3b1c" /></linearGradient>
            <linearGradient id="sRed" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#ef4444" /><stop offset="1" stopColor="#991b1b" /></linearGradient>
            <linearGradient id="sSack" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#a8784a" /><stop offset="1" stopColor="#6b4423" /></linearGradient>
            <GoldDefs id="sGold" />
        </defs>
        {[[230, 0], [285, -7], [340, -2]].map(([x, dy], i) => (
            <g key={i} transform={`translate(${x} ${dy})`}>
                <path d={`M${175 - x} ${70 - dy} L14 56`} stroke="#7c2d12" strokeWidth="1.1" />
                <g className="fx-gallop-b"><path d="M-14 62 L-34 70 L-42 66 M-10 64 L-29 76 L-37 74" stroke="#5e3b1c" strokeWidth="3.6" fill="none" strokeLinecap="round" />
                    <path d="M-42 66 L-45 64 M-37 74 L-40 73" stroke="#1c1917" strokeWidth="3.6" strokeLinecap="round" /></g>
                <path d="M-20 60 C-20 52 -8 50 4 51 C14 52 22 54 22 60 C22 66 12 69 0 69 C-12 69 -20 66 -20 60 Z" fill="url(#sDeer)" />
                <path d="M-14 64 C-4 68 8 68 16 64" stroke="#d4a373" strokeWidth="3" fill="none" />
                <path d="M13 56 L25 37 L32 40 L21 61 Z" fill="url(#sDeer)" />
                <path d="M24 31 C30 28 38 30 42 35 C44 38 40 41 34 40 C29 40 25 38 24 31 Z" fill="#8b5a2b" />
                <ellipse cx="39.5" cy="37.5" rx="3.4" ry="2.4" fill="#d4a373" />
                <path d="M27 31 L22 15 L15 11 M22 18 L28 9 M32 30 L35 14 L41 10 M35 17 L30 8" stroke="#e7cf9f" strokeWidth="2" fill="none" strokeLinecap="round" />
                <circle cx="31" cy="33" r="1.3" fill="#111" />
                <path d="M8 53 C10 49 18 49 20 53" stroke="#b91c1c" strokeWidth="2.6" fill="none" />
                <circle cx="14" cy="56" r="2" fill="url(#sGold)" />
                <g className="fx-gallop-f"><path d="M14 63 L33 71 L41 68 M10 64 L27 76 L35 76" stroke="#6b4423" strokeWidth="3.6" fill="none" strokeLinecap="round" />
                    <path d="M41 68 L44 66 M35 76 L38 75" stroke="#1c1917" strokeWidth="3.6" strokeLinecap="round" /></g>
                <ellipse cx="-21" cy="56" rx="4" ry="2.6" fill="#f5f0e6" />
                <circle cx="44" cy="37" r={i === 2 ? 3 : 2.2} fill={i === 2 ? '#ef4444' : '#1c1917'} className={i === 2 ? 'fx-rudolph2' : ''} />
            </g>
        ))}
        {/* sack + gifts */}
        <path d="M58 44 Q54 17 76 15 Q93 17 89 46 Z" fill="url(#sSack)" />
        <path d="M66 18 Q76 24 86 18" stroke="#4e2f12" strokeWidth="1.6" fill="none" />
        <rect x="62" y="6" width="12" height="11" rx="1.5" fill="#2563eb" /><path d="M68 6 L68 17" stroke="#facc15" strokeWidth="2" />
        <rect x="76" y="3" width="11" height="13" rx="1.5" fill="#16a34a" /><path d="M81.5 3 L81.5 16" stroke="#dc2626" strokeWidth="2" />
        {/* sleigh */}
        <path d="M40 70 Q36 40 54 40 L58 40 Q62 64 80 66 L160 66 Q176 66 182 52 L188 52 Q186 84 158 88 L60 88 Q40 88 40 70 Z" fill="url(#sRed)" />
        <path d="M48 76 Q100 84 172 72" stroke="url(#sGold)" strokeWidth="2.4" fill="none" />
        <path d="M46 46 Q44 70 60 82" stroke="url(#sGold)" strokeWidth="1.8" fill="none" />
        <path d="M30 102 L168 102 Q190 102 192 88 M30 102 Q21 102 23 93" stroke="url(#sGold)" strokeWidth="3.6" fill="none" strokeLinecap="round" />
        <path d="M70 88 L66 102 M140 88 L144 102" stroke="url(#sGold)" strokeWidth="2.8" />
        {/* Santa */}
        <path d="M88 66 Q90 40 106 36 Q122 40 124 66 Z" fill="url(#sRed)" />
        <path d="M106 37 L106 66" stroke="#fff" strokeWidth="3.4" />
        <rect x="89" y="54" width="34" height="5" fill="#1c1917" /><rect x="102" y="53" width="8" height="7" rx="1" fill="none" stroke="url(#sGold)" strokeWidth="1.6" />
        <circle cx="108" cy="30" r="8.5" fill="#f5c6a5" />
        <path d="M99.5 31 Q99 46 108 48 Q117 46 116.5 31 Q112 37 108 36 Q104 37 99.5 31 Z" fill="#fff" />
        <path d="M103 35 Q108 32 113 35" stroke="#fff" strokeWidth="2.6" fill="none" strokeLinecap="round" />
        <circle cx="105" cy="28.5" r="1.1" fill="#111" /><circle cx="111" cy="28.5" r="1.1" fill="#111" />
        <circle cx="108" cy="32" r="1.6" fill="#f87171" />
        <ellipse cx="103.5" cy="31.5" rx="1.8" ry="1" fill="rgba(239,68,68,.35)" /><ellipse cx="112.5" cy="31.5" rx="1.8" ry="1" fill="rgba(239,68,68,.35)" />
        <path d="M100 25 Q106 7 124 13 L129 21 Q118 17 116 24 Z" fill="url(#sRed)" />
        <path d="M99 25 L117 23" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" />
        <circle cx="131" cy="22" r="3.6" fill="#fff" />
        <path d="M118 46 L150 54" stroke="url(#sRed)" strokeWidth="5.4" strokeLinecap="round" />
        <circle cx="151" cy="54.5" r="3" fill="#1c1917" />
        <path d="M151 54 L190 56" stroke="#7c2d12" strokeWidth="1.1" />
    </svg>
);

// ── Shaded decorations ─────────────────────────────────────────────────────────────────────────
const ClayDiya = ({ scale = 1, delay = 0 }) => (
    <div className="fx-diya2" style={{ '--fx-s': scale, '--fx-d': `${delay}s` }}>
        <div className="fx-diya-halo" />
        <div className="fx-diya-flame"><span className="fx-fl-outer" /><span className="fx-fl-inner" /><span className="fx-fl-core" /></div>
        <svg viewBox="0 0 80 42">
            <defs>
                <linearGradient id="clay" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#d97706" /><stop offset=".45" stopColor="#b45309" /><stop offset="1" stopColor="#5c1f05" /></linearGradient>
                <linearGradient id="clayHi" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#fde68a" stopOpacity="0" /><stop offset=".35" stopColor="#fde68a" stopOpacity=".55" /><stop offset=".7" stopColor="#fde68a" stopOpacity="0" /></linearGradient>
            </defs>
            <path d="M4 12 Q40 6 70 10 Q80 10 77 6 Q80 14 70 16 Q62 40 36 40 Q10 40 4 12 Z" fill="url(#clay)" />
            <ellipse cx="38" cy="12" rx="32" ry="4.6" fill="#3f1404" />
            <ellipse cx="38" cy="11.4" rx="28" ry="3" fill="#7c2d12" opacity=".75" />
            <path d="M10 18 Q36 26 64 18" stroke="url(#clayHi)" strokeWidth="5" fill="none" />
            <path d="M12 24 Q38 34 62 24" stroke="#fbbf24" strokeWidth="1.4" fill="none" strokeDasharray="2 3" />
            {[22, 32, 42, 52].map(x => <circle key={x} cx={x} cy={30 + (x % 2)} r="1.4" fill="#fde68a" />)}
        </svg>
    </div>
);

const Rangoli = () => {
    const ring = (n, r, rx, ry, color, off = 0) => Array.from({ length: n }).map((_, i) => (
        <ellipse key={`${r}-${i}`} cx="60" cy={60 - r} rx={rx} ry={ry} fill={color} transform={`rotate(${i * (360 / n) + off} 60 60)`} />));
    return (
        <svg viewBox="0 0 120 120" className="fx-rangoli2">
            <defs><radialGradient id="rgC"><stop offset="0" stopColor="#fef08a" /><stop offset="1" stopColor="#ea580c" /></radialGradient></defs>
            {ring(16, 44, 7, 13, '#be185d')}
            {ring(16, 44, 4, 8, '#f472b6', 0)}
            {ring(12, 30, 7, 12, '#ea580c', 15)}
            {ring(12, 30, 3.5, 7, '#fde047', 15)}
            {ring(8, 17, 6, 9, '#15803d', 22.5)}
            {ring(24, 55, 1.8, 1.8, '#facc15', 7.5)}
            <circle cx="60" cy="60" r="9" fill="url(#rgC)" /><circle cx="60" cy="60" r="3.5" fill="#b91c1c" />
        </svg>
    );
};

const Kandil = ({ hue, side }) => (
    <div className={`fx-kandil2 fx-kandil2-${side}`}>
        <svg viewBox="0 0 70 160">
            <defs>
                <radialGradient id={`kg${side}`} cx=".5" cy=".45" r=".6"><stop offset="0" stopColor="#fff7d6" /><stop offset=".35" stopColor={hue[0]} /><stop offset="1" stopColor={hue[1]} /></radialGradient>
                <linearGradient id={`kt${side}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={hue[0]} /><stop offset="1" stopColor={hue[1]} stopOpacity=".4" /></linearGradient>
            </defs>
            <path d="M35 0 L35 30" stroke="#78350f" strokeWidth="1.4" />
            <path d="M35 30 L58 58 L66 74 L58 90 L35 116 L12 90 L4 74 L12 58 Z" fill={`url(#kg${side})`} opacity=".96" />
            <path d="M35 30 L46 74 L35 116 L24 74 Z" fill="#fff" opacity=".18" />
            <path d="M12 58 L58 58 M4 74 L66 74 M12 90 L58 90" stroke="#facc15" strokeWidth="1.6" />
            <path d="M35 30 L35 116" stroke="#facc15" strokeWidth="1" opacity=".7" />
            {[22, 28, 35, 42, 48].map((x, i) => <path key={x} d={`M${x} 112 Q${x + (i - 2) * 1.5} 134 ${x + (i - 2) * 2.5} 156`} stroke={`url(#kt${side})`} strokeWidth="2.2" fill="none" />)}
        </svg>
        <div className="fx-kandil-glow" style={{ background: `radial-gradient(circle, ${hue[0]}66 0%, transparent 70%)` }} />
    </div>
);

const LitTree = () => (
    <svg viewBox="0 0 150 210" className="fx-tree2">
        <defs>
            <linearGradient id="tg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#22c55e" /><stop offset=".55" stopColor="#15803d" /><stop offset="1" stopColor="#14532d" /></linearGradient>
            <linearGradient id="tt" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#451a03" /><stop offset=".5" stopColor="#78350f" /><stop offset="1" stopColor="#451a03" /></linearGradient>
            <radialGradient id="star" cx=".4" cy=".35"><stop offset="0" stopColor="#fffbeb" /><stop offset=".5" stopColor="#facc15" /><stop offset="1" stopColor="#b45309" /></radialGradient>
            {[['r', '#ef4444'], ['b', '#3b82f6'], ['g', '#eab308'], ['p', '#d946ef']].map(([k, c]) => (
                <radialGradient key={k} id={`orn${k}`} cx=".35" cy=".3" r=".7"><stop offset="0" stopColor="#fff" /><stop offset=".3" stopColor={c} /><stop offset="1" stopColor={c} stopOpacity=".7" /></radialGradient>
            ))}
            <radialGradient id="tglow"><stop offset="0" stopColor="#fde68a" stopOpacity=".9" /><stop offset="1" stopColor="#fde68a" stopOpacity="0" /></radialGradient>
        </defs>
        <rect x="66" y="168" width="18" height="22" fill="url(#tt)" />
        {[[75, 30, 46, 74], [75, 58, 58, 112], [75, 92, 70, 150], [75, 126, 80, 176]].map(([cx, top, half, bottom], i) => {
            const teeth = 7 + i * 2, pts = [];
            for (let j = 0; j <= teeth; j++) { const x = cx - half + (2 * half * j) / teeth; pts.push(`${x},${bottom - (j % 2 ? 6 : 0)}`); }
            return <path key={i} d={`M${cx} ${top} L${cx + half} ${bottom} L${pts.reverse().join(' L')} Z`} fill="url(#tg)" />;
        })}
        <path d="M44 84 Q75 98 106 82 M34 122 Q75 140 116 120 M28 158 Q75 178 122 156" stroke="#facc15" strokeWidth="2" fill="none" strokeDasharray="1 3.2" strokeLinecap="round" />
        {[[56, 68, 'r'], [92, 72, 'b'], [48, 104, 'g'], [78, 108, 'p'], [104, 100, 'r'], [40, 142, 'b'], [64, 146, 'r'], [94, 144, 'g'], [116, 138, 'p'], [74, 82, 'g']].map(([x, y, k], i) => (
            <g key={i}><circle className="fx-tw2" cx={x} cy={y} r="9" fill="url(#tglow)" style={{ animationDelay: `${i * 0.31}s` }} /><circle cx={x} cy={y} r="4.6" fill={`url(#orn${k})`} /></g>
        ))}
        <g className="fx-star2"><circle cx="75" cy="24" r="16" fill="url(#tglow)" />
            <path d="M75 8 L79.5 19 L91 19.5 L82 26.5 L85.5 38 L75 31.5 L64.5 38 L68 26.5 L59 19.5 L70.5 19 Z" fill="url(#star)" /></g>
        <rect x="36" y="184" width="26" height="20" rx="2" fill="#dc2626" /><path d="M49 184 L49 204 M36 193 L62 193" stroke="#fde047" strokeWidth="3" />
        <rect x="88" y="188" width="24" height="16" rx="2" fill="#2563eb" /><path d="M100 188 L100 204" stroke="#f8fafc" strokeWidth="3" />
    </svg>
);

// ── Scenes ───────────────────────────────────────────────────────────────────────────────────
const NavratriScene = ({ width, small }) => {
    const canvasRef = useFx((eng) => { eng.setAmbient('petal', small ? 9 : 20); eng.setAmbient('bokeh', small ? 6 : 14); });
    return (
        <>
            <FxCanvas canvasRef={canvasRef} />
            <MarigoldToran width={width} small={small} />
            <div className="fx-corner fx-left fx-stage">
                <div className="fx-stage-light" />
                <GarbaDancer />
                {!small && <GarbaDancer flip palette={GARBA_PALETTES[1]} />}
            </div>
            <div className="fx-corner fx-right"><MataOnLion /></div>
        </>
    );
};

// Ram ji and Ravan trade arrows: three times they meet mid-air and burst; the 4th, Ram ji's, strikes
// Ravan in the stomach — he burns, collapses, fireworks go up, a 5-second pause, then it all repeats.
const DussehraScene = ({ width, small }) => {
    const ramRef = useRef(null), ravanRef = useRef(null);
    const st = useRef({ cycle: -1, shots: 0, phase: '', lastCracker: 0 });
    const RAVAN_STATES = ['is-hit', 'is-burning', 'is-collapsing', 'is-gone', 'is-returning'];
    const setRavan = (cls) => { const el = ravanRef.current; if (!el) return; el.classList.remove(...RAVAN_STATES); if (cls) el.classList.add(cls); };
    const setDraw = (ram, rav) => {
        if (ramRef.current) ramRef.current.classList.toggle('is-drawing', ram);
        if (ravanRef.current) ravanRef.current.classList.toggle('is-drawing', rav);
    };
    const CYCLE = 22;
    const SHOTS = [1.0, 2.9, 4.8, 6.7];      // first three clash, the 4th hits
    const canvasRef = useFx(
        (eng) => eng.setAmbient('petal', small ? 4 : 8),
        (eng, sec) => {
            const s = st.current, n = Math.floor(sec / CYCLE), t = sec - n * CYCLE;
            if (n !== s.cycle) { s.cycle = n; s.shots = 0; s.phase = 'fight'; s.gone = false; setRavan(''); }
            const ram = ramRef.current && ramRef.current.getBoundingClientRect();
            const rav = ravanRef.current && ravanRef.current.getBoundingClientRect();
            if (!ram || !rav) return;
            const fly = small ? 600 : 760;
            if (s.phase === 'fight' && s.shots < SHOTS.length) {
                const due = SHOTS[s.shots], last = s.shots === SHOTS.length - 1;
                const aiming = t > due - 0.6 && t < due;
                setDraw(aiming, aiming && !last);
                if (t >= due) {
                    s.shots += 1;
                    setDraw(false, false);
                    const x0 = ram.left + ram.width * 0.9, y0 = ram.top + ram.height * 0.27;          // Ram ji's arrow tip
                    const xr = rav.left - rav.width * 0.06, yr = rav.top + rav.height * 0.38;         // Ravan's arrow tip
                    if (!last) {
                        const m = 0.42 + Math.random() * 0.16;
                        const xm = x0 + (xr - x0) * m, ym = y0 + (yr - y0) * m + (Math.random() * 16 - 8);
                        eng.arrow(x0, y0, xm, ym, fly, () => eng.clash(xm, ym));
                        eng.arrow(xr, yr, xm, ym, fly, null, { flame: '#9333ea' });
                    } else {
                        const xs = rav.left + rav.width * 0.5, ys = rav.top + rav.height * 0.52;      // stomach
                        eng.arrow(x0, y0, xs, ys, fly + 250, () => {
                            eng.impact(xs, ys, '#facc15');
                            eng.clash(xs, ys);
                            setRavan('is-hit');
                            s.phase = 'hit'; s.hitAt = st.current.lastT;
                        });
                    }
                }
            }
            s.lastT = t;
            const fireX = rav.left + rav.width * 0.5, fireY = rav.top + rav.height * 0.88;
            if (s.phase === 'hit' && t - s.hitAt > 0.35) { s.phase = 'burn'; s.burnAt = t; setRavan('is-burning'); }
            if (s.phase === 'burn') {
                const bt = t - s.burnAt;
                eng.setFire('ravan', { x: fireX, y: fireY, w: rav.width * 0.62, h: rav.height * Math.min(0.8, 0.15 + bt * 0.3), power: Math.min(1.8, 0.5 + bt * 0.6) });
                if (bt > 2.6) { s.phase = 'collapse'; s.colAt = t; setRavan('is-collapsing'); }
            }
            if (s.phase === 'collapse') {
                const ct = t - s.colAt;
                eng.setFire('ravan', ct < 2.2 ? { x: fireX, y: fireY, w: rav.width * 0.62, h: rav.height * Math.max(0.1, 0.8 - ct * 0.35), power: Math.max(0.15, 1.8 - ct * 0.75) } : null);
                if (ct > 1.5 && ct < 6 && t - s.lastCracker > (small ? 0.55 : 0.32)) {
                    s.lastCracker = t;
                    const near = Math.random() < 0.5;
                    const x = near ? fireX + rand(-rav.width, rav.width * 0.4) : rand(eng.W * 0.25, eng.W * 0.92);
                    eng.launch(Math.min(eng.W - 30, Math.max(30, x)), rand(eng.H * 0.1, eng.H * 0.45), { color: FIREWORK_COLORS[Math.floor(Math.random() * FIREWORK_COLORS.length)], scale: 1.1 });
                }
                if (ct > 1.4 && !s.gone) { s.gone = true; setRavan('is-gone'); }
                if (ct > 6) s.phase = 'pause';
            }
            // 5 s after the last cracker he rises again; the next cycle restarts the fight
            if (s.phase === 'pause' && t > CYCLE - 1.1) { s.phase = 'return'; setRavan('is-returning'); }
        });
    return (
        <>
            <FxCanvas canvasRef={canvasRef} />
            <MarigoldToran width={width} small={small} />
            <div className="fx-corner fx-left"><RamArcher innerRef={ramRef} /></div>
            <div className="fx-corner fx-right"><RavanEffigy innerRef={ravanRef} /></div>
        </>
    );
};

const DiwaliScene = ({ width, small }) => {
    const st = useRef({ next: 0.4 });
    const canvasRef = useFx(null, (eng, sec) => {
        const s = st.current;
        if (sec >= s.next) {
            if (eng.rockets.length < (small ? 3 : 5)) {
                const x = rand(eng.W * (small ? 0.1 : 0.22), eng.W * 0.92);
                eng.launch(x, rand(eng.H * 0.08, eng.H * 0.42));
                if (!small && Math.random() < 0.25) eng.launch(Math.min(eng.W - 40, x + rand(-160, 160)), rand(eng.H * 0.1, eng.H * 0.35));
            }
            s.next = sec + (small ? rand(0.8, 1.4) : rand(0.4, 0.85));
        }
    });
    return (
        <>
            <FxCanvas canvasRef={canvasRef} />
            <FairyLights width={width} small={small} warm />
            <Kandil hue={['#f97316', '#9a3412']} side="left" />
            {!small && <Kandil hue={['#ec4899', '#831843']} side="right" />}
            <div className="fx-corner fx-left fx-diyas2">
                <Rangoli />
                <ClayDiya delay={0} /><ClayDiya scale={1.2} delay={0.3} />
            </div>
            <div className="fx-corner fx-right fx-diyas2"><ClayDiya scale={1.2} delay={0.2} /><ClayDiya delay={0.5} /></div>
        </>
    );
};

const ChristmasScene = ({ width, small }) => {
    const sleighRef = useRef(null);
    const st = useRef({ lastGift: 0 });
    const canvasRef = useFx((eng) => eng.setAmbient('snow', small ? 34 : 80), (eng, sec) => {
        const el = sleighRef.current;
        if (!el) return;
        const FLY = small ? 9 : 13, PAUSE = 4.5, t = sec % (FLY + PAUSE);
        const sw = el.offsetWidth, sh = el.offsetHeight, W = eng.W;
        if (t > FLY) { el.style.opacity = '0'; return; }
        const p = t / FLY;
        const x = -sw + (W + sw) * p, y = Math.sin(p * Math.PI * 2.5) * 12 - p * 18;
        el.style.opacity = '1';
        el.style.transform = `translate(${x}px, ${y}px)`;
        const top = el.offsetTop + y;
        eng.dust(x + sw * 0.08, top + sh * 0.72);
        if (Math.random() < 0.5) eng.dust(x + sw * 0.05, top + sh * 0.6);
        if (sec - st.current.lastGift > (small ? 1.2 : 0.85) && x > 0 && x < W - sw * 0.5) {
            st.current.lastGift = sec;
            eng.gift(x + sw * 0.2, top + sh * 0.3, ((W + sw) / (FLY * 60)) * 0.35);
        }
    });
    return (
        <>
            <FxCanvas canvasRef={canvasRef} />
            <FairyLights width={width} small={small} />
            <div ref={sleighRef} className="fx-sleigh2"><SleighColour /></div>
            <div className="fx-corner fx-left"><LitTree /></div>
        </>
    );
};

// The decoration layer. Renders nothing for the default theme.
export function FestiveDecor({ theme }) {
    const w = useViewportWidth();
    const small = w < 640;
    if (!theme || theme === 'default' || !isFestiveThemeKey(theme)) return null;
    const meta = FESTIVE_THEMES.find(t => t.key === theme);
    return (
        <div className={`fx-layer fx-${theme}`} aria-hidden="true">
            {theme === 'navratri' && <NavratriScene key="n" width={w} small={small} />}
            {theme === 'dussehra' && <DussehraScene key="d" width={w} small={small} />}
            {theme === 'diwali' && <DiwaliScene key="w" width={w} small={small} />}
            {theme === 'christmas' && <ChristmasScene key="c" width={w} small={small} />}
            <Greeting text={meta.greeting} />
        </div>
    );
}

// The admin-only Theme tab.
export function ThemeSettings({ liveTheme, previewTheme, onPreview, onApply, saving }) {
    const shown = previewTheme || liveTheme || 'default';
    return (
        <div className="custom-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <h3 style={{ margin: 0 }}>Theme</h3>
                {previewTheme && previewTheme !== liveTheme && (
                    <button className="btn-custom btn-secondary-custom btn-sm" onClick={() => onPreview(null)}>Stop preview</button>
                )}
            </div>
            <p style={{ color: '#64748b', fontSize: '13px', margin: '6px 0 16px' }}>
                <b>Preview</b> shows a theme only on your screen. <b>Apply for everyone</b> switches every user's task manager
                (they see it the next time a page loads or within a few minutes). Only you can see this tab.
            </p>
            <div className="fx-theme-grid">
                {FESTIVE_THEMES.map(t => {
                    const isLive = (liveTheme || 'default') === t.key;
                    const isShown = shown === t.key;
                    return (
                        <div key={t.key} className={`fx-theme-card ${isShown ? 'is-shown' : ''}`}>
                            <div className="fx-theme-swatch" style={{ background: `linear-gradient(135deg, ${t.swatch[0]} 0%, ${t.swatch[0]} 40%, ${t.swatch[1]} 75%, ${t.swatch[2]} 100%)` }}>
                                <span className="fx-theme-icon">{t.icon}</span>
                                {isLive && <span className="fx-theme-live">LIVE</span>}
                            </div>
                            <div className="fx-theme-body">
                                <div className="fx-theme-name">{t.label}</div>
                                <div className="fx-theme-blurb">{t.blurb}</div>
                                <div className="fx-theme-actions">
                                    <button className="btn-custom btn-secondary-custom btn-sm" disabled={isShown}
                                        onClick={() => onPreview(t.key === liveTheme ? null : t.key)}>{isShown ? 'Showing' : 'Preview'}</button>
                                    <button className="btn-custom btn-primary-custom btn-sm" disabled={isLive || saving}
                                        onClick={() => onApply(t.key)}>{isLive ? 'Applied' : 'Apply for everyone'}</button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
